import React from 'react'
import { getCommonnessTier } from '../../lib/getCommonnessTier'

// The meter has one segment per commonness tier (see getCommonnessTier).
const METER_SEGMENTS = 5

type Props = {
   // 0–1; drives the friendly tier label + meter.
   commonnessScore: number
   // The "Our take" learner advice under the meter. The Thai page shows it —
   // how common a Thai word is decides whether to learn it now. The English
   // page doesn't: the reader already knows the English word, so the tier alone
   // is context for the translations, not advice.
   showVerdict?: boolean
}

// The hero's commonness card: the tier headline, the Rare–Everyday signal, and
// (on the Thai page) the "Our take" verdict, sharing one left edge. Static and
// non-interactive by design — no hover, no cursor change, no click target.
const CommonnessCard = ({ commonnessScore, showVerdict = false }: Props) => {
   const { level, label, description } = getCommonnessTier(commonnessScore)
   const filledSegments = level + 1

   return (
      <div className="flex flex-col items-start gap-[14px] bg-white border border-usageBorder rounded-2xl p-[14px_16px] sm:p-[20px_22px_22px] text-left">
         <div className="text-[22px] font-bold tracking-[-0.01em] leading-[1.15] text-bodyBlack">
            {label}
         </div>
         <div className="flex items-center justify-start gap-[11px]">
            <span className="text-[14px] font-normal text-usageLabel whitespace-nowrap">Rare</span>
            <span
               className="flex items-center gap-[3px] p-[5px_7px] rounded-[7px] bg-usageTrack"
               aria-label={`${label} word`}
            >
               {Array.from({ length: METER_SEGMENTS }).map((_, index) => (
                  <span
                     key={index}
                     className={`w-[5px] h-[18px] rounded-[3px] ${
                        index < filledSegments ? 'bg-usageBarFill' : 'bg-usageBarEmpty'
                     }`}
                  />
               ))}
            </span>
            <span className="text-[14px] font-normal text-usageLabel whitespace-nowrap">
               Everyday
            </span>
         </div>
         {/* A labelled value, not a link: the eyebrow names the line as
             metadata, which is what lets the verdict itself carry confident
             near-black weight without reading as a CTA. */}
         {showVerdict && (
            <div className="flex flex-col items-start gap-[2px]">
               <span className="text-[13px] font-bold tracking-[1.2px] uppercase text-textMuted select-none">
                  Our take
               </span>
               <span className="text-[16px] font-semibold leading-[1.3] text-bodyBlack">
                  {description}
               </span>
            </div>
         )}
      </div>
   )
}

export default CommonnessCard
